import React, { useEffect, useRef, useState } from 'react';
import { X, BookOpen, Mail, Phone, Download, Sparkles } from 'lucide-react';

export default function ExitIntentPopup() {
  const [open, setOpen] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({ email: '', phone: '' });
  const shownRef = useRef(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    // Only trigger once per session (unless user manually reopens)
    const dismissed = sessionStorage.getItem('mtm_exit_popup_dismissed');
    if (dismissed) shownRef.current = true;

    const onMouseOut = (e) => {
      if (shownRef.current) return;
      // Trigger when the mouse leaves through the top of the viewport
      if (e.clientY <= 0 && !e.relatedTarget && !e.toElement) {
        shownRef.current = true;
        setOpen(true);
      }
    };

    // Fallback for mobile / long sessions: show after 30s if not shown
    const timer = setTimeout(() => {
      if (!shownRef.current) {
        shownRef.current = true;
        setOpen(true);
      }
    }, 30000);

    document.addEventListener('mouseout', onMouseOut);
    return () => {
      document.removeEventListener('mouseout', onMouseOut);
      clearTimeout(timer);
    };
  }, []);

  const close = () => {
    setOpen(false);
    try { sessionStorage.setItem('mtm_exit_popup_dismissed', '1'); } catch (e) {}
  };

  const submit = (e) => {
    e.preventDefault();
    if (!form.email || !form.phone) return;
    setSubmitted(true);
    setTimeout(() => {
      close();
      setSubmitted(false);
    }, 2200);
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm animate-[fadeIn_.25s_ease-out]"
      onClick={close}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-lg rounded-2xl bg-gradient-to-b from-[#101a2c] to-[#0a1120] border border-emerald-500/25 shadow-2xl overflow-hidden"
      >
        {/* Decorative glow */}
        <div className="absolute -top-24 -right-24 w-64 h-64 bg-emerald-500/20 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute -bottom-24 -left-24 w-64 h-64 bg-teal-500/10 rounded-full blur-3xl pointer-events-none" />

        <button
          onClick={close}
          aria-label="Close"
          className="absolute top-3 right-3 z-10 w-8 h-8 rounded-full bg-white/5 hover:bg-white/10 text-slate-300 flex items-center justify-center transition-colors"
        >
          <X size={16} />
        </button>

        <div className="relative p-8">
          {!submitted ? (
            <>
              <div className="flex items-center gap-2 text-emerald-300 text-xs font-semibold">
                <Sparkles size={14} /> FREE DOWNLOAD &middot; LIMITED TIME
              </div>

              <div className="mt-4 w-14 h-14 rounded-xl bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center">
                <BookOpen className="text-emerald-400" size={26} />
              </div>

              <h3 className="mt-4 text-white text-2xl md:text-3xl font-extrabold leading-tight">
                Wait! Download Our Free 2026 Exam Strategy &amp; PYQ E-Book
              </h3>
              <p className="mt-3 text-slate-400 text-sm">
                Get proven strategies, previous year questions, and topper insights &mdash; delivered instantly to your inbox &amp; WhatsApp.
              </p>

              <form onSubmit={submit} className="mt-6 space-y-3">
                <div className="relative">
                  <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                  <input
                    type="email"
                    required
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="Enter your Email Address"
                    className="w-full pl-9 pr-3 py-3 rounded-md bg-[#0a1120] border border-white/10 text-white text-sm placeholder:text-slate-500 focus:outline-none focus:border-emerald-500/50 focus:ring-2 focus:ring-emerald-500/20"
                  />
                </div>
                <div className="relative">
                  <Phone size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                  <input
                    type="tel"
                    required
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    placeholder="WhatsApp / Phone Number"
                    className="w-full pl-9 pr-3 py-3 rounded-md bg-[#0a1120] border border-white/10 text-white text-sm placeholder:text-slate-500 focus:outline-none focus:border-emerald-500/50 focus:ring-2 focus:ring-emerald-500/20"
                  />
                </div>
                <button
                  type="submit"
                  className="w-full inline-flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-semibold py-3 rounded-md transition-colors shadow-lg shadow-emerald-500/25"
                >
                  <Download size={16} /> Download Free E-Book
                </button>
              </form>

              <p className="mt-4 text-center text-xs text-slate-500">
                No spam. Unsubscribe anytime. 100% free resource.
              </p>
            </>
          ) : (
            <div className="py-10 text-center">
              <div className="w-14 h-14 rounded-full bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center mx-auto">
                <Download className="text-emerald-400" size={26} />
              </div>
              <h3 className="mt-5 text-white text-2xl font-extrabold">You&apos;re all set!</h3>
              <p className="mt-2 text-slate-400 text-sm">
                Check your Email &amp; WhatsApp in a few minutes. Happy learning!
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
